import { Header, Container, ProgressContainer, BasketProgressbar, BasketProgressFill, Counter, Content, NavigationContainer, QuestionTransition, Helptext } from "./QuestionScreen.style";
import Question from "../../components/Question";
import Answer from "../../components/Answer";
import { useEffect, useState } from "react";
import { questions as behaviourQuestions, types } from "../../BehaviourAnalysis";
import Analytics from "../../../../utils/analytics";

export type SelectedAnswer = {
    questionId: number;
    answerId: string;
    answer: string;
    type: types;
};

interface Props {
    questions: typeof behaviourQuestions;
    pages: Array<{ id: number; questionId: number }>;
    onResult: (answers: SelectedAnswer[]) => void;
}

export default function QuestionScreen({ questions, pages, onResult }: Props) {
    const [questionsForCurrentPage, setQuestionsForCurrentPage] = useState<typeof questions>([]);
    const [progressPercent, setProgressPercent] = useState(0)
    const [answers, setAnswers] = useState<SelectedAnswer[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [currentQuestionId, setCurrentQuestionId] = useState<number | null>(null);
    const [shuffledAnswers, setShuffledAnswers] = useState<Record<number, typeof behaviourQuestions[number]['answers']>>({});

    //Get question for current page
    useEffect(() => {
        const questionId = pages.find(page => page.id === currentPage)?.questionId ?? null;
        setCurrentQuestionId(questionId)
        const pageQuestions = questionId !== null
            ? [questions.find(question => question.id === questionId)].filter((question): question is (typeof questions)[number] => Boolean(question))
            : [];

        setQuestionsForCurrentPage(pageQuestions);

        // Shuffle answers for this page's question (only once per question)
        if (questionId !== null) {
            setShuffledAnswers(prev => {
                if (prev[questionId]) return prev;
                const question = questions.find(q => q.id === questionId);
                if (!question) return prev;
                const shuffled = [...question.answers].sort(() => Math.random() - 0.5);
                return { ...prev, [questionId]: shuffled };
            });
        }
    }, [currentPage]);

    //Handle answer selection
    const selectAnswer = (questionId: number, answerId: string, answerText: string, answerType: types) => {
        setAnswers(prevAnswers => {
            // Single-choice per question: replace existing answer for this question.
            const filteredAnswers = prevAnswers.filter(item => item.questionId !== questionId);

            return [
                ...filteredAnswers,
                {
                    questionId,
                    answerId,
                    answer: answerText,
                    type: answerType
                }
            ];
        });
    };
    
    //Calcuclate progress for progressbar
    useEffect(() => {
        let stepPercent = (currentPage / pages.length) * 100
        setProgressPercent(stepPercent)
    }, [currentPage])

    //Handle next step
    const handleNext = () => {
        if (currentPage === pages.length) {
            // track last page answers
            Analytics.getInstance().trackBehaviourProfiling(`q${currentQuestionId}`, "behavioral", `${answers.find(answer => answer.questionId === currentQuestionId)?.type || "no_answer"}`);
            onResult(answers);
        } else {
            if(currentQuestionId !== null && answers.some(answer => answer.questionId === currentQuestionId)) {
                Analytics.getInstance().trackBehaviourProfiling(`q${currentQuestionId}`, "behavioral", `${answers.find(answer => answer.questionId === currentQuestionId)?.type || "no_answer"}`);
                setCurrentPage(currentPage + 1);
                const topOfpage = document.getElementById("question-screen-top");
                topOfpage?.scrollIntoView({ behavior: "smooth" });
            }
        }
    }

    return (
        <div id="question-screen-top">
            {questionsForCurrentPage.map(question => (
            <QuestionTransition key={question.id}>
                <Header>
                    <Container>
                        <ProgressContainer>
                            <BasketProgressbar>
                                <BasketProgressFill width={progressPercent}/>
                            </BasketProgressbar>  
                            <Counter>{`${currentPage}/${pages.length}`}</Counter>
                        </ProgressContainer>
                        <Question key={question.id} question={question.question} order={question.order} />
                        <Helptext>Vælg det svar, der passer bedst på dig.</Helptext>
                    </Container>
                </Header>
                <Content>
                    <Container>
                        {(shuffledAnswers[question.id] ?? question.answers).map(answer => (
                            <Answer
                            key={answer.id}
                            answer={answer.answer}
                            onClick={() => selectAnswer(question.id, answer.id, answer.answer, answer.value)}
                            isChecked={answers.some(item => item.questionId === question.id && item.answerId === answer.id)}
                            />
                        ))}
                        <NavigationContainer>
                            {/*  {currentPage > 1 &&
                                <Back className="underline-link" onClick={() => setCurrentPage(currentPage - 1)}>Tilbage</Back>
                            } */}
                            <button
                                className="button"
                                onClick={() => handleNext()}
                                disabled={!answers.some(answer => answer.questionId === question.id)}
                            >
                                {currentPage === pages.length ? "Se resultat" : "Næste"}
                            </button>
                        </NavigationContainer>
                    </Container>
                </Content>
                </QuestionTransition>
                ))}
            </div>
    );
}